import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useReviews } from '../context/ReviewContext';
import { useLocations } from '../context/LocationContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import TopBar from '../components/TopBar';
import { Sparkles, Send, RefreshCw, Check, Clock, AlertCircle, Crown, Play } from 'lucide-react';
import { storage } from '../utils/helpers';
import { STORAGE_KEYS } from '../utils/constants';
import '../styles/theme.css';

const Reviews = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { reviews, loading, fetchReviews, generateReply, postReply, updateReply } = useReviews();
  const { selectedLocations, fetchLocations } = useLocations();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [postingId, setPostingId] = useState(null);
  const [editingReply, setEditingReply] = useState(null);
  const [editText, setEditText] = useState('');
  const [usage, setUsage] = useState({ used: 0, limit: 5, remaining: 5, limitReached: false });
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const demoModeActive = storage.get('demo_mode');

  useEffect(() => {
    const userData = storage.get(STORAGE_KEYS.USER_DATA);
    
    if (!isAuthenticated && !demoModeActive && !userData) {
      navigate('/');
      return;
    }
    
    const loadData = async () => {
      try {
        await fetchLocations();
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadData();
  }, [isAuthenticated, navigate, fetchLocations, demoModeActive]);

  useEffect(() => {
    if (demoModeActive || selectedLocations.length > 0) {
      const locationId = selectedLocations[0]?.id || 'demo-location-1';
      setSelectedLocation(locationId);
    }
  }, [selectedLocations, demoModeActive]);

  useEffect(() => {
    if (selectedLocation || demoModeActive) {
      fetchReviews(selectedLocation).then(data => {
        if (data?.usage) {
          setUsage(data.usage);
        }
      }).catch(console.error);
    }
  }, [selectedLocation, fetchReviews, demoModeActive]);

  const handleGenerateReply = async (reviewId) => {
    // Check limit before generating
    if (usage.limitReached) {
      setShowUpgradeModal(true);
      return;
    }

    setGeneratingId(reviewId);
    const result = await generateReply(reviewId);
    setGeneratingId(null);
    
    if (result.success) {
      window.showToast?.('AI reply generated successfully!', 'success');
      if (result.usage) {
        setUsage(result.usage);
      }
    } else if (result.limitReached) {
      setUsage(prev => ({ ...prev, limitReached: true, remaining: 0 }));
      setShowUpgradeModal(true);
    } else {
      window.showToast?.(`Failed to generate reply: ${result.error}`, 'error');
    }
  };

  const handlePostReply = async (reviewId) => {
    setPostingId(reviewId);
    const result = await postReply(reviewId);
    setPostingId(null);
    
    if (result.success) {
      window.showToast?.(demoModeActive ? 'Reply posted! (Demo Mode)' : 'Reply posted successfully!', 'success');
    } else {
      window.showToast?.(`Failed to post reply: ${result.error}`, 'error');
    }
  };

  const handleEditReply = (review) => {
    setEditingReply(review.id);
    setEditText(review.generatedReply);
  };

  const handleSaveEdit = (reviewId) => {
    updateReply(reviewId, editText);
    setEditingReply(null);
    setEditText('');
    window.showToast?.('Reply updated!', 'success');
  };

  const handleCancelEdit = () => {
    setEditingReply(null);
    setEditText('');
  };

  const getReviewerInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    return names.map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'posted':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Check className="w-3 h-3" /> Posted
          </span>
        );
      case 'generated':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3" /> Ready to Post
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3" /> Pending
          </span>
        );
    }
  };

  if (!isAuthenticated && !demoModeActive) {
    return null;
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col w-full min-h-screen bg-slate-50">
      <TopBar />

      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-5xl mx-auto">
          
          {/* Demo Mode Banner */}
          {demoModeActive && (
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-4 mb-6 flex items-center justify-between shadow-lg" data-testid="demo-mode-banner">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Demo Mode Active</h3>
                  <p className="text-purple-100 text-sm">Testing with mock reviews • {usage.remaining} AI replies remaining</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg px-4 py-2">
                  <span className="text-white font-semibold">{usage.used}/{usage.limit}</span>
                  <span className="text-purple-200 text-sm ml-1">replies used</span>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade Modal */}
          {showUpgradeModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUpgradeModal(false)}>
              <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="upgrade-modal">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Free Plan Limit Reached</h2>
                  <p className="text-slate-600 mb-6">You've used all 5 free AI replies. Upgrade to Basic plan for unlimited replies!</p>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
                    <div className="text-3xl font-bold text-slate-900 mb-1">₹499<span className="text-lg text-slate-500 font-normal">/month</span></div>
                    <div className="text-slate-600 text-sm mb-4">Basic Plan</div>
                    <ul className="text-left text-sm text-slate-700 space-y-2">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Unlimited AI replies</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Up to 5 locations</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500" /> Priority support</li>
                    </ul>
                  </div>
                  
                  <Link 
                    to="/subscription" 
                    className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-3 px-6 rounded-xl hover:shadow-lg transition-all mb-3"
                    data-testid="upgrade-btn"
                  >
                    Upgrade to Basic - ₹499
                  </Link>
                  <button 
                    onClick={() => setShowUpgradeModal(false)}
                    className="text-slate-500 hover:text-slate-700 text-sm"
                  >
                    Maybe later
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Reviews</h1>
              <p className="text-sm text-slate-600 mt-1">
                {reviews.length} reviews • {reviews.filter(r => r.status === 'pending').length} pending replies
              </p>
            </div>
          </div>

          {/* Reviews List */}
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-200">
                <div className="text-6xl mb-4">💬</div>
                <p className="text-lg text-slate-600">No reviews found</p>
                <p className="text-sm text-slate-500 mt-2">Reviews will appear here once loaded</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div 
                  key={review.id} 
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
                  data-testid="review-card"
                >
                  {/* Review Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                        {getReviewerInitials(review.reviewer?.displayName)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {review.reviewer?.displayName || 'Anonymous'}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[...Array(5)].map((_, i) => (
                              <span key={i} className={i < review.starRating ? 'text-yellow-400' : 'text-slate-300'}>
                                ★
                              </span>
                            ))}
                          </div>
                          <span className="text-xs text-slate-500">
                            {new Date(review.createTime).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    {getStatusBadge(review.status)}
                  </div>

                  {/* Review Text */}
                  <p className="text-slate-700 mb-4 leading-relaxed">
                    "{review.comment || 'No comment provided'}"
                  </p>

                  {/* Generated Reply */}
                  {review.generatedReply && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">AI Generated Reply</span>
                      </div>
                      
                      {editingReply === review.id ? (
                        <div className="space-y-3">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full p-3 border border-blue-200 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={4}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(review.id)}
                              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              Save Changes
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-4 py-2 bg-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-300 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-700">{review.generatedReply}</p>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    {!review.generatedReply && (
                      <button
                        onClick={() => handleGenerateReply(review.id)}
                        disabled={generatingId === review.id}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                          usage.limitReached 
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg' 
                            : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-lg'
                        }`}
                        data-testid="generate-reply-btn"
                      >
                        {generatingId === review.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Generating...
                          </>
                        ) : usage.limitReached ? (
                          <>
                            <Crown className="w-4 h-4" />
                            Upgrade to Generate
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4" />
                            Generate AI Reply
                          </>
                        )}
                      </button>
                    )}

                    {review.generatedReply && review.status !== 'posted' && (
                      <>
                        <button
                          onClick={() => handlePostReply(review.id)}
                          disabled={postingId === review.id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="post-reply-btn"
                        >
                          {postingId === review.id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Posting...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" />
                              Post Reply
                            </>
                          )}
                        </button>

                        {editingReply !== review.id && (
                          <button
                            onClick={() => handleEditReply(review)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
                          >
                            Edit
                          </button>
                        )}

                        <button
                          onClick={() => handleGenerateReply(review.id)}
                          disabled={generatingId === review.id || usage.limitReached}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
                          data-testid="regenerate-btn"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Regenerate
                        </button>
                      </>
                    )}

                    {review.status === 'posted' && (
                      <span className="inline-flex items-center gap-2 px-4 py-2 text-green-700 text-sm font-medium">
                        <Check className="w-4 h-4" />
                        Reply Posted Successfully
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reviews;
